/* Elle Adore stone viewer — a single gem that spins on its own.
   Deliberately no drag-to-orbit or pan: a one-finger swipe always scrolls the
   page. Zoom only, via pinch (touch / trackpad), double-tap, or the +/− buttons. */
import * as THREE from "three";
import { buildBrilliant, buildPortuguese, makeStudioEnv, makeGemMaterial, makeShadowTexture } from "ea-gem-core";

// Same physics as the picker's stones; "clear" is the Classic colourless moissanite
const MOISSANITE_IOR = [2.58, 2.66, 2.80];
const TINTS = {
  clear: { tint: [0.99, 0.995, 1.0], absorb: 0.4, envBoost: 4.5 },
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 2.2;
const BASE_DIST = 4.6;
// Half-width the stone needs on screen (girdle radius 1 plus breathing room)
const FIT_HALF_WIDTH = 1.3;
// Camera looks down slightly so the crown facets read as well as the pavilion fire
const VIEW_DIR = new THREE.Vector3(0, 0.38, 1).normalize();

function initViewer(root) {
  const canvasWrap = root.querySelector("[data-ea-viewer-canvas]");
  const hintEl = root.querySelector("[data-ea-viewer-hint]");
  const cut = root.getAttribute("data-cut") === "brilliant" ? "brilliant" : "portuguese";
  const look = TINTS[root.getAttribute("data-tint")] || TINTS.clear;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  } catch (e) {
    // No WebGL: drop the whole block rather than show an empty box
    const section = root.closest("[data-ea-viewer-section]") || root;
    section.style.display = "none";
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
  const measure = () => ({
    w: Math.max(1, canvasWrap.clientWidth || 600),
    h: Math.max(1, canvasWrap.clientHeight || 420),
  });
  let { w, h } = measure();
  renderer.setSize(w, h);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  canvasWrap.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  // data-bg lets a page set its own backdrop (e.g. dark velvet); studio grey otherwise
  scene.background = new THREE.Color(root.getAttribute("data-bg") || 0xeef0f3);
  const camera = new THREE.PerspectiveCamera(30, w / h, 0.1, 100);
  // Narrow (phone) viewers back the camera off so the whole stone still fits across
  const fitDist = () => {
    const halfH = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    return Math.max(BASE_DIST, FIT_HALF_WIDTH / (halfH * camera.aspect));
  };
  let baseDist = fitDist();

  const envTex = makeStudioEnv();
  const built = cut === "brilliant" ? buildBrilliant() : buildPortuguese();
  const mesh = new THREE.Mesh(built.geo, makeGemMaterial(built.planes, envTex));
  const u = mesh.material.uniforms;
  u.uIor.value.fromArray(MOISSANITE_IOR);
  u.uTint.value.fromArray(look.tint);
  u.uAbsorb.value = look.absorb;
  u.uEnvBoost.value = look.envBoost;

  const stoneGroup = new THREE.Group();
  stoneGroup.add(mesh);
  scene.add(stoneGroup);

  const shadow = new THREE.Mesh(
    new THREE.PlaneGeometry(3.4, 3.4),
    new THREE.MeshBasicMaterial({ map: makeShadowTexture(), transparent: true, depthWrite: false })
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = cut === "portuguese" ? -1.3 : -1.15;
  scene.add(shadow);

  // Aim at the middle of the stone (crown top to culet), not the girdle
  const target = new THREE.Vector3(0, cut === "portuguese" ? -0.45 : -0.35, 0);

  // ── Zoom ────────────────────────────────────────────────────────────────
  let zoom = MIN_ZOOM;
  let shownZoom = MIN_ZOOM;
  const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
  const setZoom = (z) => {
    zoom = clampZoom(z);
    dismissHint();
  };
  const dismissHint = () => {
    if (hintEl) hintEl.classList.add("is-hidden");
  };

  root.querySelectorAll("[data-ea-zoom]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const step = btn.getAttribute("data-ea-zoom") === "in" ? 1.3 : 1 / 1.3;
      setZoom(zoom * step);
    });
  });
  canvasWrap.addEventListener("dblclick", () => {
    setZoom(zoom > (MIN_ZOOM + MAX_ZOOM) / 2 ? MIN_ZOOM : MAX_ZOOM);
  });

  // Two-finger pinch only. One finger is never intercepted, so the page scrolls.
  let pinch = null;
  const fingerGap = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  canvasWrap.addEventListener("touchstart", (e) => {
    if (e.touches.length === 2) pinch = { gap: fingerGap(e.touches), zoom };
  }, { passive: true });
  canvasWrap.addEventListener("touchmove", (e) => {
    if (!pinch || e.touches.length !== 2) return;
    e.preventDefault();
    setZoom(pinch.zoom * (fingerGap(e.touches) / pinch.gap));
  }, { passive: false });
  canvasWrap.addEventListener("touchend", (e) => {
    if (e.touches.length < 2) pinch = null;
  }, { passive: true });

  // Trackpad pinch: Chrome/Firefox send ctrl+wheel, desktop Safari sends gesture events.
  // A plain mouse wheel is left alone so it scrolls the page.
  canvasWrap.addEventListener("wheel", (e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    setZoom(zoom * Math.exp(-e.deltaY * 0.01));
  }, { passive: false });
  if (!("ontouchstart" in window)) {
    let gestureStart = zoom;
    canvasWrap.addEventListener("gesturestart", (e) => { e.preventDefault(); gestureStart = zoom; });
    canvasWrap.addEventListener("gesturechange", (e) => { e.preventDefault(); setZoom(gestureStart * e.scale); });
  }

  // ── Loop ────────────────────────────────────────────────────────────────
  const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const spinSpeed = reduceMotion ? 0.12 : 0.35;
  const clock = new THREE.Clock();
  const camObj = new THREE.Vector3();
  const nrmMat = new THREE.Matrix3();

  let running = true;
  new IntersectionObserver((entries) => {
    running = entries.some((e) => e.isIntersecting);
    if (running) clock.getDelta();
  }, { threshold: 0.05 }).observe(canvasWrap);

  let t = 0;
  function animate() {
    requestAnimationFrame(animate);
    if (!running) return;
    t += Math.min(clock.getDelta(), 0.1);

    stoneGroup.rotation.y = t * spinSpeed;
    // Gentle rock so the table and crown catch the light, not just the side
    stoneGroup.rotation.x = Math.sin(t * 0.45) * 0.14;

    shownZoom += (zoom - shownZoom) * 0.15;
    camera.position.copy(VIEW_DIR).multiplyScalar(baseDist / shownZoom).add(target);
    camera.lookAt(target);

    stoneGroup.updateMatrixWorld();
    camObj.copy(camera.position);
    mesh.worldToLocal(camObj);
    u.uCamObj.value.copy(camObj);
    nrmMat.getNormalMatrix(mesh.matrixWorld);
    u.uModel.value.copy(nrmMat);

    renderer.render(scene, camera);
  }

  function onResize() {
    const s = measure();
    w = s.w; h = s.h;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    baseDist = fitDist();
    renderer.setSize(w, h);
  }
  window.addEventListener("resize", onResize);
  if (window.ResizeObserver) new ResizeObserver(onResize).observe(canvasWrap);

  animate();
}

// Build each viewer only as it nears the screen, so the shader compile never
// slows the top of the page down
const pending = document.querySelectorAll("[data-ea-stone-viewer]");
if (pending.length) {
  const lazy = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      obs.unobserve(e.target);
      initViewer(e.target);
    });
  }, { rootMargin: "300px 0px" });
  pending.forEach((el) => lazy.observe(el));
}
